import { debounce } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch } from "react-redux";

import ExpenditureFormRow, { ALL_RESOURCE_OPTIONS } from "components/UI/_TODO/ExpenditureForm/ExpenditureFormRow";
import PopoverOverlay from "components/UI/_TODO/PopoverOverlay/PopoverOverlay";
import { templateExpendituresLocalEdit } from "pages/HandBook/model/thunks";
import {
  ICreateTemplateExpenditure,
  IHandBookTemplateExpenditure,
  IMaterialInHandbookExpenditure,
} from "pages/HandBook/model/types";

import { TableReusableCell } from "shared/ui/dataDisplay/TableReusable/TableReusableRow";
import Checkbox from "shared/ui/inputs/Checkbox/Checkbox";
import InputBase from "shared/ui/inputs/InputBase";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { EXPENDITURE_TYPES } from "constants/constant";
import { ExpenditureType } from "types/enums/ExpenditureTypeEnum";

import { getChangedFields } from "utils/formHelpers/getChangedFields";

import styles from "./AddTemplateExpenditure.module.scss";

const HANDBOOK_TEMPLATE_EXPENDITURE_REQUIRED_FIELDS = ["name", "measure", "expenditure_type"];
const RESOURCE_EXPENDITURE_TYPE_FILTER: ExpenditureType[] = ["material", "equipment", "machine", "transport"];

const expenditureUpdateFieldsFilter = (field: string) =>
  !field.startsWith("id:") && field !== "expenditure_type" && field !== "price" && field !== "count";

interface IProps {
  item: IHandBookTemplateExpenditure;
  /** ID подраздела шаблона */
  sectionId: number;
  displayEl: (
    el: IHandBookTemplateExpenditure | IMaterialInHandbookExpenditure,
    onDelete?: () => void
  ) => React.ReactNode;
  columns: any;
  colPercents: number[];
  isChecked: boolean;
  onCheckItem: () => void;
}

const AddTemplateExpenditure: React.FC<IProps> = ({
  item,
  sectionId,
  displayEl,
  columns,
  colPercents,
  isChecked,
  onCheckItem,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef<HTMLFormElement>(null);

  const [materials, setMaterials] = useState<number[]>([]);
  const [deletedMaterials, setDeletedMaterials] = useState<Record<number, boolean>>({});
  const [materialsFromHandbook, setMaterialsFromHandbook] = useState<Record<number, boolean>>({});
  const [manualMaterials, setManualMaterials] = useState<Record<number, boolean>>({});

  const initialValues = useMemo(
    () =>
      Object.assign({}, item.manual_expenditure || {}, {
        expenditure_type: EXPENDITURE_TYPES.WORK,
      }),
    [item]
  );

  const submit = (values: any) => {
    // Ресурсы, которых нет в справочнике. Сначала добавляются в справочник, затем связываются с работой
    const newManualMaterials: Omit<ICreateTemplateExpenditure, "manual_expenditure_id">[] = [];
    // Ресурсы по справочнику
    const newMaterialsFromHandbook: ICreateTemplateExpenditure[] = [];
    materials
      .filter((m) => !deletedMaterials[m])
      .forEach((id) => {
        const { _handbookExpenditure, ...materialFields } = values[`id:${id}`] || {};
        if (!materialFields) return;
        const materialManualExpenditure = {
          id: materialFields.id,
          type: materialFields.expenditure_type,
          name: materialFields.name,
          measure: materialFields.measure,
          count: materialFields.count || 0,
          price: materialFields.price || 0,
        };

        // Фильтр новых материалов по id < 0 или изменённому материалу
        if (
          !_handbookExpenditure ||
          (_handbookExpenditure &&
            Object.keys(getChangedFields(_handbookExpenditure, materialFields)).filter(expenditureUpdateFieldsFilter)
              .length > 0)
        ) {
          newManualMaterials.push({
            section_id: sectionId,
            related_work_id: item.id,
            manual_expenditure: materialManualExpenditure,
            price: materialManualExpenditure.price,
            count: materialManualExpenditure.count,
          });
          setManualMaterials((prev) => ({ ...prev, [id]: true }));
          setMaterialsFromHandbook((prev) => ({ ...prev, [id]: false }));
        } else if (_handbookExpenditure) {
          newMaterialsFromHandbook.push({
            section_id: sectionId,
            manual_expenditure_id: _handbookExpenditure.id,
            related_work_id: item.id,
            price: materialManualExpenditure.price,
            count: materialManualExpenditure.count,
          });
          setManualMaterials((prev) => ({ ...prev, [id]: false }));
          setMaterialsFromHandbook((prev) => ({ ...prev, [id]: true }));
        }
      });
    // Фильтр существующих материалов по id > 0
    const materialsToDelete = Object.keys(deletedMaterials)
      .map((id) => parseFloat(id))
      .filter((id) => id > 0);
    if (newManualMaterials.length || newMaterialsFromHandbook.length || materialsToDelete.length) {
      dispatch(
        templateExpendituresLocalEdit({
          expenditureId: item.id,
          newManualMaterials,
          newMaterialsFromHandbook,
          materialsToDelete,
        })
      );
    }
  };

  return (
    <Form
      onSubmit={submit}
      key={initialValues?.id}
      initialValues={initialValues}
      render={({ values, handleSubmit, form }) => {
        const delayedSubmit = debounce(() => form.submit(), 50);
        return (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onReset={() => form.restart()}
            onInput={() => form.getState().dirty && delayedSubmit()}
            onClick={() => form.getState().dirty && delayedSubmit()}
            className={styles.form}
          >
            <div className={styles.formWrapper}>
              <RowGrid percents={colPercents}>
                <Checkbox onCheck={onCheckItem} checked={isChecked} />
                {/* @ts-ignore */}
                {columns.map((el, index) =>
                  index === 0 ? (
                    <div className={styles.name}>
                      <InputBase disabled value={form.getState().values["name"]} />
                      <div
                        className={styles.add}
                        onClick={() => {
                          const mockId = -Date.now();
                          form.change(`id:${mockId}`, { expenditure_type: "material", type: "material" });
                          setMaterials((prev) => [...prev, mockId]);
                          setManualMaterials((prev) => ({ ...prev, [mockId]: true }));
                        }}
                      >
                        <PopoverOverlay
                          openType="hover"
                          placement="top"
                          popoverBorderColor="primary"
                          content={<div className={styles.popover}>Добавить связанный ресурс</div>}
                        >
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5 2.41406H11.5V11.4141H2.5V13.4141H11.5V22.4141H13.5V13.4141H22.5V11.4141H13.5V2.41406Z"
                              fill="#4FB1EB"
                            />
                          </svg>
                        </PopoverOverlay>
                      </div>
                    </div>
                  ) : (
                    <TableReusableCell
                      isCentered={el.isCentered}
                      isNoBreak={el.isNoBreak}
                      isRighted={el.isRighted}
                      key={index}
                    >
                      {/* @ts-ignore */}
                      {el.render(item)}
                    </TableReusableCell>
                  )
                )}
              </RowGrid>
              {item.materials
                .filter((m) => !deletedMaterials[m.id])
                .map((material) =>
                  displayEl(material, () => {
                    setMaterials((prev) => prev.filter((m) => m !== material.id));
                    setDeletedMaterials((prev) => ({ ...prev, [material.id]: true }));
                    delayedSubmit();
                  })
                )}
              {materials
                .filter((id) => !deletedMaterials[id])
                .map((el) => (
                  <div key={el} className={styles.newMaterialRow}>
                    <ExpenditureFormRow
                      submit={() => form.submit()}
                      requiredFields={HANDBOOK_TEMPLATE_EXPENDITURE_REQUIRED_FIELDS}
                      resourceOpitons={ALL_RESOURCE_OPTIONS}
                      onCancel={() => {
                        setMaterials((prev) => prev.filter((m) => m !== el));
                        setDeletedMaterials((prev) => ({ ...prev, [el]: true }));
                        delayedSubmit();
                      }}
                      isWork={false}
                      id={el}
                      showLabels={false}
                      gridClassName={styles.materialGrid}
                      onSelectExpenditure={(exp) => {
                        // @ts-ignore
                        form.change(`id:${el}`, { expenditure_type: exp.type, ...exp, _handbookExpenditure: exp });
                        setMaterialsFromHandbook((prev) => ({ ...prev, [el]: true }));
                        delayedSubmit();
                      }}
                      key={form.getState().values[`id:${el}`]?.expenditure_type}
                      expType={form.getState().values[`id:${el}`]?.expenditure_type || "material"}
                      form={form}
                      isWithNumberVariant={false}
                      classNameAutocompleteContainer={
                        manualMaterials[el]
                          ? styles.newManualMaterialLabel
                          : materialsFromHandbook[el]
                          ? styles.newHandbookMaterialLabel
                          : undefined
                      }
                      classNameActions={styles.deleteAction}
                    />
                  </div>
                ))}
            </div>
          </form>
        );
      }}
    />
  );
};

export default AddTemplateExpenditure;
